import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Box,
  Button,
  Paper,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import Preloader from 'components/Preloader';
import CustomTextInput from 'components/Forms/CustomTextInput';
import ConfirmPromptModal from 'components/ConfirmPromptModal/ConfirmPromptModal';
import { updateMenuSettings } from 'redux/actions/Menu/menu';

import ChannelRow from './ChannelRow';
import { buildSettingsPayload, loadWebsiteSettings } from './api';
import {
  TRACKING_MODE,
  buildTrackingMetadata,
  defaultTrackingForm,
  isAdvanced,
  parseTrackingMetadata,
  trackingSchema,
} from './model';

// CustomTextInput only renders the helper-text node when it has content, so a
// field with no example text grows the moment a validation message appears and
// pushes everything below it down. Reserving the line as padding on the field
// itself and floating the message into it keeps the outer height constant
// whether the field is clean, showing an example, or showing an error.
const HELPER_LINE = 24;

const fieldSx = {
  width: '100%',
  position: 'relative',
  pb: `${HELPER_LINE}px`,
  '& .MuiFormHelperText-root': {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    m: 0,
    lineHeight: 1.4,
  },
};

const ModeChoice = ({ selected, title, description, onSelect }) => (
  <Box
    role="radio"
    aria-checked={selected}
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect();
      }
    }}
    sx={{
      flex: 1,
      minWidth: 240,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      p: 2,
      cursor: 'pointer',
      borderRadius: 2,
      border: '1px solid',
      borderColor: selected ? 'primary.main' : 'divider',
      backgroundColor: selected ? 'action.hover' : 'transparent',
    }}
  >
    <Radio checked={selected} tabIndex={-1} />
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </Box>
);

const Tracking = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // The settings object this screen loaded, sent back on save with only
  // metadata changed.
  const settingsRef = useRef({});
  const legacyGtmIdRef = useRef(null);

  const formMethods = useForm({
    defaultValues: defaultTrackingForm,
    mode: 'all',
    resolver: yupResolver(trackingSchema),
  });
  const {
    control,
    reset,
    setValue,
    handleSubmit,
    formState: { isDirty },
  } = formMethods;

  const values = useWatch({ control });
  const advanced = isAdvanced(values);

  useEffect(() => {
    let active = true;
    loadWebsiteSettings()
      .then(({ settings, metadata }) => {
        if (!active) {
          return;
        }
        settingsRef.current = settings;
        legacyGtmIdRef.current = metadata?.GTMID ?? null;
        reset(parseTrackingMetadata(metadata));
      })
      .catch(() => {
        if (active) {
          toast.error(t('website_settings.tracking.load_failed'));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [reset, t]);

  const onSubmit = (formValues) => {
    setSaving(true);
    const metadata = buildTrackingMetadata(formValues, {
      legacyGtmId: legacyGtmIdRef.current,
    });

    dispatch(
      updateMenuSettings(
        buildSettingsPayload(settingsRef.current, metadata),
        'website'
      )
    )
      .then(() => {
        settingsRef.current = {
          ...settingsRef.current,
          metadata: { ...(settingsRef.current.metadata ?? {}), ...metadata },
        };
        // Re-seed defaults so the form is clean again after saving.
        reset(formValues);
        setSaved(true);
      })
      .catch(() => toast.error(t('website_settings.tracking.save_failed')))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return <Preloader />;
  }

  const setField = (name) => (value) => {
    setValue(name, value, { shouldDirty: true, shouldValidate: true });
    setSaved(false);
  };

  return (
    <FormProvider {...formMethods}>
      <ConfirmPromptModal hasUnsavedChanges={isDirty} />
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t('website_settings.tracking.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('website_settings.tracking.subtitle')}
        </Typography>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>
              {t('website_settings.tracking.setup')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('website_settings.tracking.setup_desc')}
            </Typography>
            <Box
              role="radiogroup"
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}
            >
              <ModeChoice
                selected={!advanced}
                title={t('website_settings.tracking.regular_mode')}
                description={t('website_settings.tracking.regular_mode_desc')}
                onSelect={() => setField('trackingMode')(TRACKING_MODE.REGULAR)}
              />
              <ModeChoice
                selected={advanced}
                title={t('website_settings.tracking.advanced_mode')}
                description={t('website_settings.tracking.advanced_mode_desc')}
                onSelect={() =>
                  setField('trackingMode')(TRACKING_MODE.ADVANCED)
                }
              />
            </Box>
          </Box>

          {advanced ? (
            <Box sx={{ px: 3, py: 2.5 }}>
              <Typography sx={{ fontWeight: 700 }}>
                {t('website_settings.tracking.gtm')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('website_settings.tracking.gtm_desc')}
              </Typography>
              <CustomTextInput
                name="gtm"
                inputProps={{ placeholder: 'GTM-XXXXXXX' }}
                label={t('website_settings.tracking.gtm_container_id')}
                helperText={t('website_settings.tracking.gtm_example')}
                sx={fieldSx}
              />
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('website_settings.tracking.legacy_note')}
              </Alert>
            </Box>
          ) : (
            <>
              <ChannelRow
                title={t('website_settings.tracking.google')}
                description={t('website_settings.tracking.google_desc')}
                checked={!!values.enableGoogle}
                onChange={setField('enableGoogle')}
              >
                <CustomTextInput
                  name="ga4"
                  inputProps={{ placeholder: 'G-XXXXXXXXXX' }}
                  label={t('website_settings.tracking.ga4_label')}
                  helperText={t('website_settings.tracking.ga4_example')}
                  sx={fieldSx}
                />

                <Box
                  sx={{
                    mt: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <ChannelRow
                    title={t('website_settings.tracking.ads')}
                    description={t('website_settings.tracking.ads_desc')}
                    checked={!!values.enableAds}
                    onChange={setField('enableAds')}
                    divider={false}
                  >
                    <Stack spacing={2}>
                      <CustomTextInput
                        name="conversionId"
                        inputProps={{ placeholder: '18481545247' }}
                        label={t('website_settings.tracking.conversion_id')}
                        sx={fieldSx}
                      />
                      <CustomTextInput
                        name="conversionLabel"
                        inputProps={{ placeholder: 'AbCdEfGhIjKlMnOp' }}
                        label={t('website_settings.tracking.conversion_label')}
                        sx={fieldSx}
                      />
                    </Stack>
                  </ChannelRow>
                </Box>
              </ChannelRow>

              <ChannelRow
                title={t('website_settings.tracking.meta')}
                description={t('website_settings.tracking.meta_desc')}
                checked={!!values.enableMeta}
                onChange={setField('enableMeta')}
              >
                <CustomTextInput
                  name="metaPixelId"
                  inputProps={{ placeholder: '123456789012345' }}
                  label={t('website_settings.tracking.pixel_id')}
                  sx={fieldSx}
                />
              </ChannelRow>

              <ChannelRow
                title={t('website_settings.tracking.tiktok')}
                description={t('website_settings.tracking.tiktok_desc')}
                checked={!!values.enableTikTok}
                onChange={setField('enableTikTok')}
              >
                <CustomTextInput
                  name="tiktokPixelId"
                  inputProps={{ placeholder: 'CDEFG12345' }}
                  label={t('website_settings.tracking.pixel_id')}
                  sx={fieldSx}
                />
              </ChannelRow>
            </>
          )}

          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'action.hover',
            }}
          >
            <Typography
              variant="body2"
              color={isDirty ? 'text.secondary' : 'success.main'}
              sx={{ mr: 'auto' }}
            >
              {isDirty
                ? t('website_settings.tracking.unsaved')
                : saved
                ? t('website_settings.tracking.saved')
                : ''}
            </Typography>
            <Button
              variant="outlined"
              disabled={!isDirty || saving}
              onClick={() => {
                reset();
                setSaved(false);
              }}
            >
              {t('website_settings.tracking.reset')}
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {t('website_settings.tracking.save')}
            </Button>
          </Box>
        </Paper>

        <Box
          component="pre"
          sx={{
            m: '10px 0 0',
            p: '14px',
            backgroundColor: '#111827',
            color: '#dce7f7',
            borderRadius: '10px',
            overflow: 'auto',
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          {JSON.stringify(
            {
              metadata: buildTrackingMetadata(values, {
                legacyGtmId: legacyGtmIdRef.current,
              }),
            },
            null,
            2
          )}
        </Box>
      </Box>
    </FormProvider>
  );
};

export default Tracking;
